import { HashMap, Identificable } from 'shared/types/commonView';
import { DocumentationModel } from 'shared/domain/documentation/documentationModel';
import { SyncStatus } from 'shared/domain/entitySyncStatus/syncStatus';
import { failUpload } from 'serviceWorker/repository/document/uploadConfirm';
import {
  PostFile,
  uploadFile,
} from 'serviceWorker/repository/file/postFile';
import { isAbortError } from 'serviceWorker/repository/httpRequest/abortRequest';
import {
  addRemoteIdToDocumentation,
  AddRemoteIdToDocumentation,
  addRemoteVersionIdToDocumentation,
  AddRemoteVersionIdToDocumentation,
  addVersionIdToDocumentation,
  AddVersionIdToDocumentation,
} from './addRemoteIdToDocumentation';
import { hardRemoveDocumentation } from './hardRemoveDocumentation';
import {
  setDocumentationSync,
  SetDocumentationSync,
} from './setDocumentationSync';
import { setVersionSync, SetVersionSync } from './setVersionSync';
import { UploadStatus } from 'shared/types/uploadStatus';
import { setUploadStatus } from './setUploadStatus';
import {
  broadcastFailUploadedDocumentation,
  broadcastUploadedDocumentation,
} from 'serviceWorker/broadcasts/documentations';
import { revokeBlobUrlsSafely } from 'shared/utils/blobUrl';
import { makeNewVersionCurrent, NewVersionSource } from './setCurrentVersion';
import { isUnsentVersion } from 'serviceWorker/db/documentations/documentationVersions';

export interface IDocumentationUploader {
  POST: (
    url: string,
    data: any,
    abortController?: AbortController
  ) => Promise<any>;
  GET: (url: string) => Promise<any>;
  DELETE: (url: string) => Promise<any>;
  POST_FILE: PostFile;
  PUT: (url: string, data: any) => Promise<any>;
}

export type UploadDocumentation = (
    abortController: AbortController,
    uploader: IDocumentationUploader,
    documentation: DocumentationModel,
    url: string
  ) => Promise<boolean>;

// export for testing purposes
export function makeUploadDocumentation(
  addRemoteIdToDocumentation: AddRemoteIdToDocumentation,
  addVersionIdToDocumentation: AddVersionIdToDocumentation,
  addRemoteVersionIdToDocumentation: AddRemoteVersionIdToDocumentation,
  setDocumentationSync: SetDocumentationSync,
  setVersionSync: SetVersionSync
): UploadDocumentation {
  return async function _uploadDocumentation(
    abortController: AbortController,
    uploader: IDocumentationUploader,
    documentation: DocumentationModel,
    url: string
  ): Promise<boolean> {
    // The file picked on this device; a documentation may have other versions already (PT-7117).
    const version = documentation.versions.find(isUnsentVersion);
    let createdDocumentationId;
    try {
      const createdDocumentation = await createDocumentation(
        abortController,
        uploader,
        documentation,
        url
      );
      createdDocumentationId = createdDocumentation._id;

      await addRemoteIdToDocumentation(
        documentation.localId,
        createdDocumentationId
      );
      await setDocumentationSync(
        documentation.localId,
        SyncStatus.SUCCESS
      );
    } catch (e) {
      await setDocumentationSync(documentation.localId, SyncStatus.FAIL);
      if (isAbortError(e)) {
        revokeBlobUrlsSafely([version?.localData || '']);
        await hardRemoveDocumentation(documentation.localId);
      }

      throw e;
    }

    if (!version?.localData) {
      await hardRemoveDocumentation(documentation.localId);
      broadcastFailUploadedDocumentation(documentation.localId);
      throw new Error('No documentation data to upload.');
    }
    const { localData } = version;
    const imageData: Response = await fetch(localData).catch(
      async () => {
        await hardRemoveDocumentation(documentation.localId);
        broadcastFailUploadedDocumentation(documentation.localId);
        throw new Error('Cannot access documentation data to upload.');
      }
    );
    const imageBlob: Blob = await imageData.blob();
    const fileName = documentation.name;
    const file = new File([imageBlob], fileName, {
      type: version.type,
    });

    const signedData = await getSignedUrlForUpload(
      uploader,
      `${url}/${createdDocumentationId}/upload`,
      version
    );
    // The backend appends the uploaded version: it is the last one (PT-7117: there may be others).
    const versionId = signedData.dto.versions.at(-1)?._id;
    if (!versionId) {
      throw new Error('The backend created no version for the upload.');
    }
    await addVersionIdToDocumentation(documentation.localId, versionId);

    try {
      const result = await uploadFile(
        abortController,
        uploader.POST_FILE,
        signedData.presignedPost.fields,
        signedData.presignedPost.url,
        file
      ).then(async (response) => {
        if (response.ok) {
          const remoteAwsVersionId =
            response.headers.get('x-amz-version-id');
          await addRemoteVersionIdToDocumentation(
            documentation.localId,
            versionId,
            remoteAwsVersionId
          );
          return {
            remoteVersionId: remoteAwsVersionId,
          };
        }
        throw new Error('Post file failed.');
      });

      await confirmUpload(
        uploader,
        `${url}/${createdDocumentationId}/version/${versionId}`,
        result.remoteVersionId
      );
      await setUploadStatus(documentation.localId, versionId, UploadStatus.success);
      await setVersionSync(documentation.localId, versionId, SyncStatus.SUCCESS);
      revokeBlobUrlsSafely([localData]);
      await makeNewVersionCurrent((changes) => uploader.PUT(`${url}/${createdDocumentationId}`, changes), {
        documentationId: createdDocumentationId,
        versionId,
        source: NewVersionSource.upload,
      });
      broadcastUploadedDocumentation({
        localId: documentation.localId,
        _id: createdDocumentationId,
        versionId: versionId,
        parentId: documentation.parentId,
      });
      return true;
    } catch (e) {
      await setVersionSync(documentation.localId, versionId, SyncStatus.FAIL);
      await failUpload(
        uploader,
        `${url}/${createdDocumentationId}/version/${versionId}`
      );
      if (isAbortError(e)) {
        revokeBlobUrlsSafely([localData]);
        await hardRemoveDocumentation(documentation.localId);
        return false;
      }
      broadcastFailUploadedDocumentation(documentation.localId);
    }
    return false;
  };
}

export const uploadDocumentation: UploadDocumentation =
  makeUploadDocumentation(
    addRemoteIdToDocumentation,
    addVersionIdToDocumentation,
    addRemoteVersionIdToDocumentation,
    setDocumentationSync,
    setVersionSync
  );

/** The backend's answer to an upload request: the documentation with the version it appended, and where to post the file. */
interface SignedVersionUpload {
  dto: { versions: Identificable[] };
  presignedPost: { url: string; fields: HashMap<string> };
}

function getSignedUrlForUpload(
  uploader: IDocumentationUploader,
  url: string,
  version: { name: string; extension: string }
): Promise<SignedVersionUpload> {
  return uploader.PUT(url, { name: version.name, extension: version.extension });
}

function createDocumentation(
  abortController: AbortController,
  uploader: IDocumentationUploader,
  documentation: DocumentationModel,
  url: string
): Promise<any> {
  const parentIdObj = documentation.parentId
    ? { parentId: documentation.parentId }
    : {};

  return uploader.POST(
    url,
    {
      name: documentation.name,
      number: documentation.number,
      ...parentIdObj,
    },
    abortController
  );
}

function confirmUpload(
  uploader: IDocumentationUploader,
  url: string,
  remoteVersionId: string
): Promise<any> {
  return uploader.PUT(`${url}/uploadConfirmation`, { remoteVersionId });
}
